import { HandPointUp } from "@gravity-ui/icons";
import { Button, Flex, Icon, Text } from "@gravity-ui/uikit";
import { useAtom } from "@reatom/npm-react";
import {
  ProductSourceDto,
  ProductSourceDtoSourceEnum,
} from "definitions/product-storage";
import { useMemo } from "react";
import { SourcePicker } from "src/components";
import { SOURCE_INFO } from "src/constants/sources";
import {
  PRICE_LOADABLE_SOURCES,
  priceLoadedAtAtom,
} from "src/pages/ProductDetailPage/store/fetchers/getProductDetailWithPrice";

import { formatPriceAge } from "../../../../utils";
import { getPlatformAvailability } from "./getPlatformAvailability";
import { useProductPriceSourceTranslate } from "./hooks";
import css from "./ProductPriceSource.module.scss";

interface ProductPriceSourceProps {
  source?: ProductSourceDto[];
  platform: ProductSourceDtoSourceEnum | null;
  setPlatform: (platform: null | ProductSourceDtoSourceEnum) => void;
  reload: () => void;
  isDesktop: boolean;
}

export const ProductPriceSource = ({
  source,
  platform,
  setPlatform,
  reload,
  isDesktop,
}: ProductPriceSourceProps) => {
  const translations = useProductPriceSourceTranslate();
  const [loadedAt] = useAtom(priceLoadedAtAtom);

  const { platforms: availablePlatforms, unavailable: unavailablePlatforms } =
    useMemo(
      () => getPlatformAvailability(source, PRICE_LOADABLE_SOURCES),
      [source],
    );

  const unavailableHint = unavailablePlatforms.length
    ? translations.unavailable_platforms(
        unavailablePlatforms.map((code) => SOURCE_INFO[code].label).join(", "),
      )
    : undefined;

  const priceAge = formatPriceAge(loadedAt, Date.now(), translations.price_age);

  return (
    <Flex direction="column" gap="2">
      <Flex direction="row" alignItems="center" gap="3">
        <Flex direction="column">
          <Text variant={isDesktop ? "subheader-2" : "subheader-3"}>
            {translations.title_price}
          </Text>
          {priceAge && (
            <Text variant="caption-2" color="secondary">
              {translations.price_loaded_ago(priceAge)}
            </Text>
          )}
        </Flex>
        <Button size="s" onClick={reload}>
          {translations.update_price}
        </Button>
      </Flex>

      {isDesktop ? (
        <SourcePicker
          variant="buttons"
          sources={availablePlatforms}
          disabledSources={unavailablePlatforms}
          disabledHint={unavailableHint}
          value={platform}
          onChange={setPlatform}
          allowDeselect
        />
      ) : (
        <>
          {availablePlatforms.length > 0 && (
            <Flex>
              <SourcePicker
                sources={availablePlatforms}
                disabledSources={unavailablePlatforms}
                disabledHint={unavailableHint}
                value={platform}
                onChange={setPlatform}
              />
            </Flex>
          )}
          {!platform && availablePlatforms.length > 0 && (
            <Flex alignItems="center" gap="2" className={css.promptHint}>
              <Icon data={HandPointUp} size={20} />
              <Text>{translations.select_platform_hint}</Text>
            </Flex>
          )}
        </>
      )}

      {unavailableHint && (
        <Text variant="caption-2" color="secondary">
          {unavailableHint}
        </Text>
      )}
    </Flex>
  );
};
